import React, { useState } from "react";
import Topnav from "../common/Topnav";
import Footer from "../common/Footer";
import { Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import bannerImage from "../../Images/student-services/program-eligibilty-banner.webp";
import axios from "axios";

// Dropdown options for eligibility
const eligibilityOptions = [
  "10+2 in Science Stream",
  "10+2 in Science Stream (with respective subjects)",
  "10+2 or Equivalent",
  "10+2 or equivalent (Any Stream)",
  "10+2 or Equivalent (in science stream)",
  "10+2 or Equivalent (in Science Stream, Biology as Core Subject)",
  "10+2 or Equivalent (Proposed)",
  "10+2 or Equivalent (with respective subjects)",
  "10+2 PCM/ITI (2 Years) (2 years Vocational Course in Relevant Trade/3 year Apprenticeship)",
  "10th or Equivalent",
  "B-Pharma (with minimum 50% Marks)",
  "B. Tech (in respective specialization)",
  "B.Lib",
  "Diploma in Engineering (3 Years) (in respective specialization from recognized institute)",
  "Graduation",
  "Graduation in Any Stream",
  "Graduation in relevant field",
  "Graduation or Equivalent",
  "Graduation or Equivalent (Pass with Maths as core subject)",
  "Graduation or Equivalent (with 2 Years work experience)",
  "Graduation or Equivalent (with respective subjects)",
  "Graduation with Science (In related field of specialization)",
  "Graduation with Science (with respective subjects)",
  "LLB",
  "Post Graduation",
];

// Group courses by school name
const groupCoursesBySchool = (data) => {
  return data.reduce((acc, course) => {
    const school = course.school_name || "Other";
    if (!acc[school]) acc[school] = [];
    acc[school].push(course);
    return acc;
  }, {});
};

// Format readable school name
const formatSchoolName = (name) =>
  name.replace(/[-_]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const ProgrammesAndEligibilities = () => {
  const [selected, setSelected] = useState("");
  const [eligibilityData, setEligibilityData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSelectChange = async (e) => {
    const selectedValue = e.target.value;
    setSelected(selectedValue);
    setEligibilityData([]);
    setError("");

    if (!selectedValue) return;

    setLoading(true);
    try {
      const encodedValue = encodeURIComponent(selectedValue);
      const response = await axios.get(
        `https://isbmuniversity.edu.in/admin-portal/eligibilityCourseList/${encodedValue}`
      );

      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
          ? response.data
          : [];

      setEligibilityData(data);
    } catch (err) {
      console.error("Error fetching eligibility:", err);
      setError("Failed to fetch eligibility. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <title>Unlock Your Future: ISBM University Programs & Eligibility</title>
      <meta
        name="description"
        content="Explore ISBM University’s innovative programs and eligibility requirements. These programs aim to equip students with essential knowledge, skills, and careers."
      />
      <Topnav />

      {/* Hero Banner */}
      <section
        className="section-hero-banner ptb-50"
        style={{
          backgroundImage: `linear-gradient(rgba(28,36,71,0.8), rgba(77,77,77,0.8)), url(${bannerImage})`,
        }}
      >
        <div className="banner-hero-overlay">
          <div className="container-xxl">
            <div className="section-hero-content text-center text-white">
              <h1>Programmes and Eligibilities</h1>
              <ol className="breadcrumb mt-3 mb-0 justify-content-center">
                <li className="breadcrumb-item">
                  <Link to="/" className="text-white text-decoration-none">
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item active text-white">
                  Programmes and Eligibilities
                </li>
                <hr className="section-divider" />
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="ptb-50">
        <div className="container-xxl">
          <Row className="justify-content-center mb-2">
            <Col lg={12} className="text-center">
              <h2 className="section-title modern-title text-blue">
                <i className="ti ti-book me-2"></i>
                Programmes by Eligibilities – Shape Your Future with ISBM University
              </h2>
            </Col>
          </Row>
          <p>
            Admissions to undergraduate programs such as B.A., B.Sc., B.Com., B.Tech., and other UG-level
            degrees at ISBM University are largely merit-based. Applicants are required to have passed their
            10+2 examination (or an equivalent) from a recognized board. For technical programs like B.Tech,
            it’s typically expected that you studied Physics, Chemistry, and Mathematics in your
            higher secondary education, with an aggregate score of around 50% . Selection is based on
            academic performance in Class 12, and some courses may consider performance in relevant
            entrance exams or conduct document verification and interviews during the admission process
          </p>
          <p>
            For postgraduate and doctoral programs, the standards vary. To be eligible for an MBA,
            candidates usually need a bachelor’s degree in any discipline from a recognized university,
            with a minimum aggregate of 50% (general category), though this may be lower for reserved
            categories. Final-year students are often eligible to apply, provided they meet the academic
            threshold by the time of admission. The selection process may also include group discussions
            and personal interviews, and tests like CAT are sometimes considered. Admission to M.A., M.Sc.,
            M.Com., PG diplomas, and Ph.D. programs is similarly guided by qualifications—such as a relevant
            bachelor’s or master’s degree with needed marks—typically evaluated on a merit basis, sometimes
            involving entrance exams for research-level courses
          </p>
        </div>
      </section>

      {/* Dropdown and Results */}
      <div className="container-xxl mb-5">
        <div className="mx-auto p-4 border rounded-5 shadow bg-white">
          <h2 className="fs-5 fw-semibold text-dark mb-3">Check Eligibility Criteria</h2>

          <select
            className="form-select mb-3"
            onChange={handleSelectChange}
            value={selected}
            disabled={loading}
          >
            <option value="">-- Select Program --</option>
            {eligibilityOptions.map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>

          {loading && <p className="text-primary">Loading eligibility info...</p>}
          {error && <p className="text-danger">{error}</p>}
        </div>

        {/* Course Results */}
        {Array.isArray(eligibilityData) && eligibilityData.length > 0 && (
          <div className="row mt-4">
            {Object.entries(groupCoursesBySchool(eligibilityData)).map(
              ([schoolName, courses]) => (
                <div
                  key={schoolName}
                  className="col-12 mb-4 p-4 bg-light border rounded-5 shadow"
                >
                  <h5 className="fw-bold text-decoration-underline mb-3">
                    {formatSchoolName(schoolName)}
                  </h5>
                  <ul className="list-unstyled row">
                    {courses.map((course) => (
                      <li key={course.id} className="col-md-4 mb-2">
                        <Link
                          to={`/course-details/${course.name}`}
                          target="_blank"
                          className="text-decoration-none text-dark"
                        >
                          <i className="ti ti-arrow-right me-1"></i>
                          {course.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            )}
          </div>
        )}

        {/* No results */}
        {Array.isArray(eligibilityData) &&
          eligibilityData.length === 0 &&
          selected &&
          !loading &&
          !error && (
            <p className="text-center text-muted mt-4">
              No courses found for this eligibility.
            </p>
          )}
      </div>

      <Footer />
    </>
  );
};

export default ProgrammesAndEligibilities;
